import { defineMessages, type IntlShape } from 'react-intl';
import type { ToolCallDisplay } from '../../../contracts/tool-call-display';

export const computerUseTaskDisplayMessages = defineMessages({
  defaultGoal: {
    id: 'RhfeHQ2OLl',
    defaultMessage: 'Driving the desktop to handle this request.',
  },
});

type ParsedComputerUseTaskKey =
  | 'mode'
  | 'task'
  | 'appTarget'
  | 'preparedInputs'
  | 'semanticMilestones'
  | 'successCriteria'
  | 'stopConditions'
  | 'nonGoals';

export type ParsedComputerUseTaskDetailKey =
  | 'mode'
  | 'appTarget'
  | 'preparedInputs'
  | 'stopConditions'
  | 'nonGoals';

export interface ParsedComputerUseTaskDetail {
  key: ParsedComputerUseTaskDetailKey;
  value: string;
}

export interface ParsedComputerUseTaskInput {
  task?: string;
  successCriteria?: string;
  milestones: string[];
  details: ParsedComputerUseTaskDetail[];
}

const DETAIL_SECTION_ORDER: ParsedComputerUseTaskDetailKey[] = [
  'mode',
  'appTarget',
  'preparedInputs',
  'stopConditions',
  'nonGoals',
];

export const parseComputerUseTaskInput = (
  raw: string | undefined
): ParsedComputerUseTaskInput | undefined => {
  if (!raw?.trim()) return undefined;

  const normalizedRaw = raw.replace(/\\n/g, '\n');
  const lines = normalizedRaw
    .split('\n')
    .map((line) => line.trimEnd())
    .filter((line) => line.trim().length > 0);

  const values = new Map<ParsedComputerUseTaskKey, string>();
  const milestones: string[] = [];
  let currentKey: ParsedComputerUseTaskKey | undefined;
  let sawStructuredSection = false;

  const appendValue = (key: ParsedComputerUseTaskKey, value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    values.set(key, [values.get(key), trimmed].filter(Boolean).join('\n'));
  };

  const appendMilestone = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    const numbered = splitNumberedMilestones(trimmed);
    milestones.push(...(numbered ?? [trimmed]));
  };

  for (const line of lines) {
    const sectionMatch = line.match(/^([A-Za-z][A-Za-z0-9 /_-]*):\s*(.*)$/);
    const sectionKey = sectionMatch?.[1]
      ? computerUseSectionKey(sectionMatch[1])
      : undefined;

    if (sectionKey) {
      sawStructuredSection = true;
      currentKey = sectionKey;
      const sectionValue = sectionMatch?.[2] ?? '';
      if (sectionKey === 'semanticMilestones') {
        appendMilestone(sectionValue);
      } else {
        appendValue(sectionKey, sectionValue);
      }
      continue;
    }

    if (!currentKey) continue;
    if (currentKey === 'semanticMilestones') {
      const trimmed = line.trim();
      if (LEADING_MILESTONE_MARKER.test(trimmed)) {
        appendMilestone(trimmed);
      } else if (milestones.length > 0) {
        milestones[milestones.length - 1] =
          `${milestones[milestones.length - 1]} ${trimmed}`;
      } else {
        appendMilestone(trimmed);
      }
      continue;
    }
    appendValue(currentKey, line);
  }

  if (!sawStructuredSection) return undefined;

  const details = DETAIL_SECTION_ORDER.flatMap((key) => {
    const value = values.get(key)?.trim();
    return value
      ? [
          {
            key,
            value,
          },
        ]
      : [];
  });

  return {
    task: values.get('task')?.trim(),
    successCriteria: values.get('successCriteria')?.trim(),
    milestones,
    details,
  };
};

/** `(` for `(1)`, `)` for `1)`, `.` for `1.`. */
type MilestoneMarkerStyle = '(' | ')' | '.';

/** A list marker at the start of a milestone: `(1) `, `1) ` or `1. `. */
const LEADING_MILESTONE_MARKER = /^(?:\((\d+)\)|(\d+)([.)]))\s+/;

/**
 * Splits one milestone string into steps. The model numbers its milestones in
 * three styles: `(1) a (2) b`, `1) a 2) b` and `1. a 2. b`. A later marker
 * starts a new step only when it repeats the first marker's style and carries
 * the next number in sequence, so `(see 3)` or `12)` inside a step stays text.
 *
 * Inline `N.` markers are not split: `Use version 2. Then enter 1.5` reads the
 * same as a two-step list, and a wrong split misstates the plan. Multiline
 * `1.` lists still split, one line at a time, in `parseComputerUseTaskInput`.
 */
const splitNumberedMilestones = (value: string): string[] | undefined => {
  const leading = value.match(LEADING_MILESTONE_MARKER);
  if (!leading) return undefined;

  let rest = value.slice(leading[0].length);
  const style: MilestoneMarkerStyle = leading[1] ? '(' : leading[3] === '.' ? '.' : ')';
  if (style === '.') return rest.trim() ? [rest.trim()] : undefined;

  const steps: string[] = [];
  let next = Number(leading[1] ?? leading[2]) + 1;
  let match = rest.match(inlineMilestoneMarker(style, next));
  while (match?.index !== undefined) {
    steps.push(rest.slice(0, match.index).trim());
    rest = rest.slice(match.index + match[0].length);
    next += 1;
    match = rest.match(inlineMilestoneMarker(style, next));
  }
  steps.push(rest.trim());

  const nonEmpty = steps.filter(Boolean);
  return nonEmpty.length > 0 ? nonEmpty : undefined;
};

const inlineMilestoneMarker = (style: MilestoneMarkerStyle, ordinal: number): RegExp =>
  style === '('
    ? new RegExp(`\\s\\(${ordinal}\\)\\s+`)
    : new RegExp(`\\s${ordinal}\\)\\s+`);

const computerUseSectionKey = (
  rawLabel: string
): ParsedComputerUseTaskKey | undefined => {
  const normalized = rawLabel
    .trim()
    .toUpperCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');

  switch (normalized) {
    case 'MODE':
      return 'mode';
    case 'TASK':
      return 'task';
    case 'APP/TARGET':
    case 'APP TARGET':
      return 'appTarget';
    case 'PREPARED INPUTS':
      return 'preparedInputs';
    case 'SEMANTIC MILESTONES':
      return 'semanticMilestones';
    case 'SUCCESS CRITERIA':
      return 'successCriteria';
    case 'STOP CONDITIONS':
      return 'stopConditions';
    case 'NON GOALS':
      return 'nonGoals';
  }
  return undefined;
};

export const getComputerUseTaskDisplay = (
  task: string | undefined,
  intl: IntlShape,
  toolCallDisplay?: ToolCallDisplay,
  fallbackPlanSteps?: readonly string[]
): {
  goal: string;
  planSteps: readonly string[] | undefined;
} => {
  const defaultGoal = intl.formatMessage(computerUseTaskDisplayMessages.defaultGoal);
  const rawGoal = task?.trim() || defaultGoal;
  const parsedTask = parseComputerUseTaskInput(rawGoal);
  const displayTitle = toolCallDisplay?.title?.trim();
  const taskGoal = parsedTask ? parsedTask.task || defaultGoal : rawGoal;
  return {
    // Older servers send the tool's generic display name here. Prefer the
    // actual task in that case so the card says what Heidi is doing.
    goal:
      displayTitle && !isGenericComputerUseTitle(displayTitle) ? displayTitle : taskGoal,
    planSteps: toolCallDisplay?.planSteps?.length
      ? toolCallDisplay.planSteps
      : parsedTask?.milestones.length
        ? parsedTask.milestones
        : fallbackPlanSteps,
  };
};

const isGenericComputerUseTitle = (title: string): boolean =>
  [
    'working the screen',
    'run computer use task',
    'computer use task',
    'computer use',
  ].includes(title.trim().toLowerCase());
